import { Link } from 'react-router-dom'
import { useOverview } from '../api/hooks'
import type { OverviewStats } from '../api/client'

// ── Stat card ─────────────────────────────────────────────────────────────────

function StatCard({
  label, value, sub, accent,
}: {
  label: string
  value: number | string
  sub?: string
  accent?: 'green' | 'red' | 'yellow' | 'blue' | 'gray'
}) {
  const ring: Record<string, string> = {
    green:  'border-green-200  bg-green-50',
    red:    'border-red-200    bg-red-50',
    yellow: 'border-yellow-200 bg-yellow-50',
    blue:   'border-indigo-200 bg-indigo-50',
    gray:   'border-gray-100   bg-white',
  }
  const text: Record<string, string> = {
    green:  'text-green-700',
    red:    'text-red-700',
    yellow: 'text-yellow-700',
    blue:   'text-indigo-700',
    gray:   'text-gray-900',
  }
  const a = accent ?? 'gray'
  return (
    <div className={`border rounded-xl p-5 shadow-sm ${ring[a]}`}>
      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">{label}</p>
      <p className={`text-3xl font-bold ${text[a]}`}>{value}</p>
      {sub && <p className="text-xs text-gray-400 mt-1">{sub}</p>}
    </div>
  )
}

// ── Severity badge ────────────────────────────────────────────────────────────

function SeverityBadge({ severity }: { severity: string }) {
  const map: Record<string, string> = {
    critical: 'bg-red-100 text-red-700',
    warning:  'bg-yellow-100 text-yellow-700',
    info:     'bg-blue-100 text-blue-700',
  }
  return (
    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${map[severity] ?? map.info}`}>
      {severity}
    </span>
  )
}

// ── Status dot ────────────────────────────────────────────────────────────────

function StatusDot({ status }: { status: string }) {
  const color: Record<string, string> = {
    online:   'bg-green-500',
    offline:  'bg-red-500',
    degraded: 'bg-yellow-500',
  }
  return (
    <span className={`inline-block w-2 h-2 rounded-full mr-1.5 ${color[status] ?? 'bg-gray-400'}`} />
  )
}

function timeSince(iso: string): string {
  const secs = Math.floor((Date.now() - new Date(iso).getTime()) / 1000)
  if (secs < 60)    return `${secs}s ago`
  if (secs < 3600)  return `${Math.floor(secs / 60)}m ago`
  if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`
  return `${Math.floor(secs / 86400)}d ago`
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function OverviewPage() {
  const { data, isLoading, isError } = useOverview()

  if (isLoading) return (
    <div className="px-8 py-6 text-sm text-gray-400">Loading overview…</div>
  )
  if (isError || !data) return (
    <div className="px-8 py-6 text-sm text-red-600">Failed to load overview.</div>
  )

  return <Overview stats={data} />
}

function Overview({ stats: s }: { stats: OverviewStats }) {
  const onlinePct = s.total_devices > 0
    ? Math.round((s.online_devices / s.total_devices) * 100)
    : 0

  return (
    <div className="px-8 py-6 space-y-8">
      <div>
        <h1 className="text-xl font-semibold text-gray-900">Overview</h1>
        <p className="text-sm text-gray-400 mt-0.5">Fleet health at a glance · refreshes every 30 s</p>
      </div>

      {/* ── Fleet health bar ────────────────────────────────────── */}
      {s.total_devices > 0 && (
        <div className="bg-white border border-gray-100 rounded-xl p-5 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">Fleet health</span>
            <span className="text-sm font-semibold text-gray-900">{onlinePct}% online</span>
          </div>
          <div className="h-3 bg-gray-100 rounded-full overflow-hidden flex">
            <div
              className="bg-green-500 transition-all duration-700"
              style={{ width: `${onlinePct}%` }}
            />
            {s.offline_devices > 0 && (
              <div
                className="bg-red-400 transition-all duration-700"
                style={{ width: `${Math.round((s.offline_devices / s.total_devices) * 100)}%` }}
              />
            )}
          </div>
          <div className="flex gap-4 mt-2 text-xs text-gray-500">
            <span><span className="inline-block w-2 h-2 rounded-full bg-green-500 mr-1" />{s.online_devices} online</span>
            <span><span className="inline-block w-2 h-2 rounded-full bg-red-400 mr-1" />{s.offline_devices} offline</span>
            {s.unknown_devices > 0 && (
              <span><span className="inline-block w-2 h-2 rounded-full bg-gray-300 mr-1" />{s.unknown_devices} unknown</span>
            )}
          </div>
        </div>
      )}

      {/* ── Stat cards ──────────────────────────────────────────── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Total devices"   value={s.total_devices}   accent="gray"   sub={`${s.online_devices} online`} />
        <StatCard label="Offline now"     value={s.offline_devices}  accent={s.offline_devices > 0 ? 'red' : 'gray'} />
        <StatCard label="Alerts today"    value={s.alerts_today}     accent={s.alerts_today > 0 ? 'yellow' : 'gray'} />
        <StatCard label="Open alerts"     value={s.open_alerts}      accent={s.open_alerts > 0 ? 'red' : 'green'} sub="unresolved" />
      </div>

      {/* ── Two-column feed ─────────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

        {/* Recent devices */}
        <div className="bg-white border border-gray-100 rounded-xl shadow-sm overflow-hidden">
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
            <h2 className="text-sm font-semibold text-gray-800">Recent devices</h2>
            <Link to="/devices" className="text-xs text-indigo-600 hover:underline">View all</Link>
          </div>
          {s.recent_devices.length === 0 ? (
            <div className="px-5 py-8 text-sm text-gray-400 text-center">No devices yet.</div>
          ) : (
            <ul className="divide-y divide-gray-50">
              {s.recent_devices.map(d => (
                <li key={d.id} className="flex items-center justify-between px-5 py-3 hover:bg-gray-50/50 transition-colors">
                  <div className="flex items-center gap-2 min-w-0">
                    <StatusDot status={d.status} />
                    <Link
                      to={`/devices/${d.id}`}
                      className="font-mono text-xs text-indigo-600 hover:underline truncate"
                    >
                      {d.external_id}
                    </Link>
                  </div>
                  <span className="text-xs text-gray-400 shrink-0 ml-3">{timeSince(d.last_seen)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Recent alerts */}
        <div className="bg-white border border-gray-100 rounded-xl shadow-sm overflow-hidden">
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
            <h2 className="text-sm font-semibold text-gray-800">Recent alerts</h2>
            <Link to="/alerts" className="text-xs text-indigo-600 hover:underline">View all</Link>
          </div>
          {s.recent_alerts.length === 0 ? (
            <div className="px-5 py-8 text-center">
              <p className="text-sm text-gray-400">No alerts fired yet.</p>
              <p className="text-xs text-gray-300 mt-1">Your fleet is quiet 🎉</p>
            </div>
          ) : (
            <ul className="divide-y divide-gray-50">
              {s.recent_alerts.map(a => (
                <li key={a.id} className="px-5 py-3 hover:bg-gray-50/50 transition-colors">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <SeverityBadge severity={a.severity} />
                      <span className="text-xs text-gray-700 truncate">{a.message}</span>
                    </div>
                    <span className="text-xs text-gray-400 shrink-0">{timeSince(a.fired_at)}</span>
                  </div>
                  {a.resolved && (
                    <span className="text-xs text-green-600 mt-0.5 block">✓ resolved</span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>

      </div>
    </div>
  )
}
